import { AudioService } from 'Modules/softphone/services/AudioService';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { createLogger } from 'Modules/softphone/utils/logger';

const logger = createLogger('CallManager:InboundAudioManager');

export class InboundAudioManager {
  /**
   * Determine which audio to play based on active calls and other inbound calls
   * - If there are active calls (ongoing/established), play 'secondIncoming' (chime)
   * - Otherwise, play 'ringtone' (full ringtone) - even if multiple inbound calls
   *
   * Per spec FR4: "If more than 1 call is received simultaneously, only the first audio
   * is played, and should continue if the first call is canceled or denied while the
   * second call waits to be picked up"
   */
  determineAudioType(): 'ringtone' | 'secondIncoming' {
    const softphoneStore = useSoftphoneStore.getState();
    const hasActiveCalls = softphoneStore.activeCalls.size > 0;

    // Only use chime if already on an active call
    if (hasActiveCalls) {
      return 'secondIncoming';
    }

    // Use ringtone for all inbound calls when not on an active call
    return 'ringtone';
  }

  /**
   * Play inbound call audio
   * Ensures the correct audio plays and stops any conflicting audio
   */
  playAudio(audioType: 'ringtone' | 'secondIncoming'): void {
    const audioService = AudioService.getInstance();

    // Stop the other inbound audio type to avoid conflicts
    if (audioType === 'ringtone') {
      void audioService.stop('secondIncoming');
    } else {
      void audioService.stop('ringtone');
    }

    // Only play if not already playing
    const isPlaying = audioService.isPlaying(audioType);
    if (!isPlaying) {
      void audioService.play(audioType);
      logger.debug('Playing inbound audio', { audioType });
    } else {
      logger.debug('Inbound audio already playing', { audioType });
    }
  }

  /**
   * Update audio playback based on active inbound calls
   * Called when an inbound call is dismissed/declined to continue audio for remaining calls
   *
   * Per spec FR4: Multiple simultaneous inbound calls should continue playing ringtone
   * (not chime) unless user is on an active call
   */
  updateAudioPlayback(): void {
    const audioService = AudioService.getInstance();
    const softphoneStore = useSoftphoneStore.getState();

    if (softphoneStore.incomingCalls.size === 0) {
      // No more inbound calls, stop all inbound audio
      audioService.stopAllInbound();
      logger.debug('Stopped all inbound audio - no remaining inbound calls');
    } else {
      // Still have inbound calls, ensure appropriate audio is playing
      const hasActiveCalls = softphoneStore.activeCalls.size > 0;

      if (hasActiveCalls) {
        // On active call - use chime
        void audioService.stop('ringtone');
        void audioService.play('secondIncoming') as Promise<void>;
        logger.debug('Continuing secondIncoming audio (on active call)', {
          remainingInboundCount: softphoneStore.incomingCalls.size,
        });
      } else {
        // No active calls - use ringtone (even for multiple inbound calls)
        void audioService.stop('secondIncoming');
        void audioService.play('ringtone');
        logger.debug('Continuing ringtone for remaining inbound calls', {
          remainingInboundCount: softphoneStore.incomingCalls.size,
        });
      }
    }
  }
}
